import { DropdownMenu } from "@/components/DropdownMenu";
import { Button } from "@/components/ui/button";
import InfoBulle from "@/components/ui/info-bulle";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { IMPORTABLE_TABLES, ImportTable } from "@/lib/import/import-types";
import { tabMenuKeyToTranslationKey } from "@/lib/utils";
import { useTranslation } from "react-i18next";

/**
 * Etape 1 : quel type de fiche importer.
 *
 * Le selecteur reprend `tabMenuItems` + `tabMenuKeyToTranslationKey`, comme la
 * page d'export : meme vocabulaire, donc rien de nouveau a apprendre.
 */
export const ImportStepTable = ({
  table,
  onTableChange,
  onNextStep,
}: {
  table: ImportTable | null;
  onTableChange: (table: ImportTable) => void;
  onNextStep: () => void;
}) => {
  const { t } = useTranslation();
  const vt = useVisibleT();

  // Temporairement, seule « Œuvre » est proposée à l'import (cf.
  // `IMPORTABLE_TABLES`) : les autres types restent gérés par le back-office
  // mais sont masqués du sélecteur.
  const visibleTables: ImportTable[] = [...IMPORTABLE_TABLES];

  const options = visibleTables.map((key) => ({
    label: t(tabMenuKeyToTranslationKey(key)),
    value: key,
  }));
  // `indexOf` peut valoir -1 (table non proposée) : on le ramène à `null` pour
  // ne pas indexer `data[-1]` dans le menu et provoquer un crash.
  const matchIndex = table ? visibleTables.indexOf(table) : -1;
  const selectedIndex = matchIndex >= 0 ? matchIndex : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        {/* Sélecteur du type de fiche (à gauche). */}
        <div className="flex flex-col gap-2 shrink-0 lg:w-72">
          <label className="text-sm font-medium">
            {vt("bo.import.table.label", "Type de fiche à importer")}
          </label>
          <DropdownMenu
            placeholder={vt("bo.import.table.placeholder", "Choisissez un type de fiche")}
            index={selectedIndex}
            data={options}
            onSelectChange={(value) => onTableChange(value as ImportTable)}
            className="w-full"
          />
        </div>

        {/* Conseils (au centre) : un seul type par import, puis la recommandation
            de tester sur un petit échantillon avant l'import complet. */}
        <div className="flex flex-1 flex-col gap-3">
          <InfoBulle
            message={vt(
              "bo.import.table.oneTypeOnly",
              "Un import = un seul type de fiche. Pour importer des œuvres et leurs auteurs, faites deux imports : d'abord les auteurs, puis les œuvres.",
            )}
          />
          <InfoBulle
            textClassName="font-semibold"
            message={vt(
              "bo.import.table.testFirst",
              "Conseil : lancez d'abord un import de test sur une dizaine d'enregistrements pour vérifier le mapping et les liaisons, avant de procéder à l'import complet.",
            )}
          />
        </div>

        {/* Bouton Continuer (à droite). */}
        <div className="shrink-0 lg:self-start">
          <Button
            disabled={!table}
            onClick={onNextStep}
            className="w-full lg:w-auto"
          >
            {vt("bo.import.continue", "Continuer")}
          </Button>
        </div>
      </div>
    </div>
  );
};
